import { useId, useState, type ReactNode } from 'react';
import { Button, Input } from '@/shared/ui/atoms';
import type { CandidateQueryDetail, CandidateQueryState } from '@/entities/outreach-candidate';
import { CandidateQueryProgress } from './CandidateQueryProgress';
import * as s from './CandidateCriteriaBar.css';

/** 화면 초기값 — IA §3-3. 바꿀 수 있다. */
export const DEFAULT_MIN_FOLLOWERS = 10_000;

export interface CandidateCriteriaBarProps {
  /**
   * 줄 맨 앞의 나라 칩. 나라를 고르는 자리는 그 칩 하나다(퀘스트 2f546c56 B안) — 이 줄은
   * 나라를 따로 고르지 않는다.
   */
  countrySlot?: ReactNode;
  /** 조회 상태. `null` 은 아직 한 번도 안 돌린 것이다. */
  state: CandidateQueryState | null;
  queryId?: string | null;
  requestedAt?: string | null;
  progress?: CandidateQueryDetail['progress'];
  progressUpdatedAt?: number;
  onSubmit: (input: { minFollowers: number; maxFollowers?: number }) => void;
  onRetry: () => void;
  /** 상세 조회 요청 자체가 실패했다. 끝난 상태가 안 와서 폴링도 멈춰 있다. */
  loadFailed?: boolean;
  /** 같은 조회를 한 번 더 묻는다. 크롤러에 재조회를 거는 `onRetry` 와 다르다. */
  onReload?: () => void;
  disabled?: boolean;
}

function parseFollowers(raw: string): number | null {
  const digits = raw.replace(/[,\s]/g, '');
  if (digits.length === 0) return null;
  const parsed = Number(digits);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * 조건 줄 — 설계 `docs/design/ia/creator-recruitment-ia.md` §3-3.
 *
 * 나라 칩 · 팔로워 하한·상한 · 조회 단추가 **한 줄**에 선다(퀘스트 2f546c56 B안). 조회 결과의
 * 수(조건에 맞는 인원 · 리전 미상)와 조회 시각은 선택 줄(`CandidateSelectionBar`)이 든다 — 같은
 * 숫자를 두 곳에 적던 것을 걷었다.
 *
 * **상태를 지어내지 않는다.** 조회는 풀스캔이라 준비까지 2분 남짓이고(2026-09-22 실측
 * 124.6~148.7초) 그동안 결과 수가 없다. 그 사이에는 이 줄 아래에 진행 문장을 세우고, 준비되면
 * 그 자리를 선택 줄이 받는다.
 */
export function CandidateCriteriaBar({
  countrySlot,
  state,
  queryId,
  requestedAt,
  progress,
  progressUpdatedAt,
  onSubmit,
  onRetry,
  loadFailed = false,
  onReload,
  disabled = false,
}: CandidateCriteriaBarProps) {
  const [minRaw, setMinRaw] = useState(String(DEFAULT_MIN_FOLLOWERS));
  const [maxRaw, setMaxRaw] = useState('');
  const followersId = useId();

  const minFollowers = parseFollowers(minRaw);
  const maxFollowers = parseFollowers(maxRaw);
  const rangeInverted =
    minFollowers !== null && maxFollowers !== null && maxFollowers < minFollowers;
  const canSubmit = minFollowers !== null && !rangeInverted && !disabled;
  const running = state === 'queued' || state === 'running';
  const hasMessage = running || loadFailed || state === 'failed' || state === 'expired';

  return (
    <section className={s.bar} aria-labelledby="candidate-criteria-heading">
      <h3 id="candidate-criteria-heading" className={s.srOnly}>
        후보 조건
      </h3>

      <div className={s.fields}>
        {countrySlot}

        <div className={s.range} role="group" aria-labelledby={followersId}>
          <span id={followersId} className={s.label}>
            팔로워
          </span>
          <label className={s.srOnly} htmlFor="candidate-min-followers">
            팔로워 하한
          </label>
          <Input
            id="candidate-min-followers"
            inputSize="xs"
            inputMode="numeric"
            className={s.input}
            value={minRaw}
            onChange={(event) => setMinRaw(event.target.value)}
            disabled={disabled}
          />
          <span className={s.label} aria-hidden="true">
            ~
          </span>
          <label className={s.srOnly} htmlFor="candidate-max-followers">
            팔로워 상한
          </label>
          <Input
            id="candidate-max-followers"
            inputSize="xs"
            inputMode="numeric"
            className={s.input}
            placeholder="없음"
            value={maxRaw}
            onChange={(event) => setMaxRaw(event.target.value)}
            error={rangeInverted ? '상한은 하한보다 작을 수 없습니다.' : undefined}
            disabled={disabled}
          />
        </div>

        <Button
          variant={state !== null && running ? 'secondary' : 'primary'}
          size="xs"
          disabled={!canSubmit}
          onClick={() =>
            minFollowers !== null &&
            onSubmit({
              minFollowers,
              maxFollowers: maxFollowers ?? undefined,
            })
          }
        >
          {state === null ? '후보 조회' : '다시 조회'}
        </Button>
      </div>

      {hasMessage ? (
        <div className={s.result}>
          {running ? (
            <CandidateQueryProgress
              key={`${queryId ?? ''}:${requestedAt ?? ''}`}
              state={state}
              queryId={queryId}
              requestedAt={requestedAt}
              progress={loadFailed ? null : progress}
              progressUpdatedAt={progressUpdatedAt}
            />
          ) : null}

          {loadFailed ? (
            <p className={s.failed} role="alert">
              조회 결과를 불러오지 못했습니다. 잠시 뒤 다시 불러오거나 새로 조회해 주세요.{' '}
              <Button variant="secondary" size="xs" onClick={onReload}>
                다시 불러오기
              </Button>
            </p>
          ) : null}

          {state === 'failed' ? (
            <p className={s.failed}>
              조회가 끝나지 못했습니다. 다시 시도하면 같은 조건으로 한 번 더 훑습니다.{' '}
              <Button variant="secondary" size="xs" onClick={onRetry}>
                다시 시도
              </Button>
            </p>
          ) : null}

          {state === 'expired' ? (
            <p className={s.failed}>
              이 조회는 만료됐습니다. 고른 후보는 그대로 남아 있고, 목록을 다시 보려면 새로 조회해야
              합니다.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
